/**
 * Adaptador de rótulos do M3 sobre o catálogo de domínio (GUPM-171).
 * Fonte da verdade dos códigos/níveis: `src/domain/tarp/catalog.ts`.
 * Aqui só ficam rótulos concisos para dropdowns/listas (o título completo
 * do catálogo é longo). A UI rica de execução do fluxo é GUPM-173.
 */
import {
  ehProtocoloGenericoDeInstrumento, nomeDoTipoDeInstrumento, TARP_CODES, tarpEntry, type TarpCode,
} from '@/domain';
import { formatarNumeroTarp } from '@/domain/tarp/numero';
import { LEVEL_UI, type TarpLevel } from '@/ui';
import type { QuickAction } from '@/components/tarp';

const SHORT_LABEL: Record<string, string> = {
  'TARP-PZ-AT': 'Piezômetro / INA — Atenção',
  'TARP-PZ-AL': 'Piezômetro / INA — Alerta',
  'TARP-MS-AL': 'Marco superficial — Alerta',
  'TARP-VIS-AL': 'Anomalia visual',
  // GUPM-299: era 'Emergência (NE-3)' — a CLASSIFICAÇÃO, não o evento. Os irmãos
  // deste mapa descrevem o que aconteceu, e o card já comunica o nível pelo ícone
  // 🚨 e pela cor, então "(NE-3)" só empurrava a descrição para fora da tela mais
  // crítica do app. Forma canônica no protótipo V8 (ADR-0018): "Situação de Ruptura
  // em Curso"; o botão que abre o protocolo já diz "EMERGÊNCIA · Ruptura em curso".
  'TARP-EMG': 'Ruptura em curso',
  'TARP-PS-INST': 'Perda de sinal — instrumento',
  'TARP-PS-CAM': 'Perda de sinal — câmeras',
  'TARP-PS-TILT': 'Perda de sinal — tiltímetros',
  'TARP-INST-EMG': 'Instrumentação — Emergência',
  // C-88 (ADR-0038)
  'TARP-INST-AT': 'Instrumento — Atenção',
  'TARP-INST-AL': 'Instrumento — Alerta',
  'TARP-TILT-EMG': 'Tiltímetro — Emergência',
};

export interface TarpCodeStub {
  code: string;
  label: string;
  nivel: TarpLevel;
}

export const TARP_CODES_STUB: TarpCodeStub[] = TARP_CODES.map((code) => ({
  code,
  label: SHORT_LABEL[code] ?? code,
  nivel: tarpEntry(code)?.nivel ?? 'ATENCAO',
}));

/**
 * Rótulo curto do protocolo. C-88: nos protocolos genéricos de instrumento o tipo vem do
 * instrumento da TARP ("Tiltímetro — Alerta") — o código diz o protocolo, o rótulo diz o
 * tipo, como o protótipo mostra "TARP-MS-AL · MSE-03". Sem tipo, o rótulo genérico.
 */
export function tarpCodeLabel(code: string, instrumentType?: string | null): string {
  const tipo = ehProtocoloGenericoDeInstrumento(code) ? nomeDoTipoDeInstrumento(instrumentType) : null;
  if (tipo) return `${tipo} — ${LEVEL_UI[tarpCodeLevel(code)].label}`;
  return SHORT_LABEL[code] ?? code;
}

export function tarpCodeLevel(code: string): TarpLevel {
  return tarpEntry(code)?.nivel ?? 'ATENCAO';
}

/**
 * QuickAction sintetizada para um `tarpCode` derivado (GUPM-185) — usada quando a
 * abertura vem por notificação e o protocolo (ex.: TARP-PZ-AL/AT) NÃO está entre
 * os 5 acionamentos manuais (`QUICK_ACTIONS`). O modal deriva título/subtítulo/
 * fluxo de `tarpEntry(code)`; aqui só os rótulos de card (label/cor).
 */
export function quickActionForCode(code: TarpCode): QuickAction {
  return {
    code,
    // `iconName` não é renderizado no caminho de auto-open (o AbrirTarpModal não exibe
    // esse campo para a QuickAction sintetizada); 'no-signal' é placeholder herdado —
    // trocar por ícone dedicado do protocolo se algum dia passar a ser exibido (review #101).
    iconName: 'no-signal',
    label: tarpCodeLabel(code),
    sub: '',
    color: LEVEL_UI[tarpCodeLevel(code)].color,
  };
}

/**
 * Identificador da TARP. `num` (TR-0001) é o rótulo curto exibido no card; `full`
 * é o nome autoexplicativo pra rastreio/export/busca (nº + barragem + instrumento
 * + protocolo, ex.: TR-0001-B-PZ-08-PZ-AT). Derivado: o banco só guarda `seq`;
 * o resto vem de dam_code / instrument_code / tarp_code.
 */
export function tarpDisplayId(
  seq: number,
  damCode: string,
  tarpCode: string,
  instrumentCode?: string | null,
): { num: string; full: string } {
  const num = formatarNumeroTarp(seq);
  // Com instrumento, o tipo do protocolo (PZ) é redundante com o instrumento
  // (PZ-08) → usa instrumento + só a severidade/subtipo (AT/AL/EMG/CAM…).
  // Sem instrumento (visual/emergência), mantém o protocolo completo (VIS-AL).
  const nivel = tarpCode.split('-').pop() ?? '';
  const descritor = instrumentCode ? `${instrumentCode}-${nivel}` : tarpCode.replace(/^TARP-/, '');
  const parts = [damCode, descritor].filter(Boolean);
  return { num, full: [num, ...parts].join('-') };
}
